import { useCRT } from "../hooks";
import { Container, Text } from "./uikit";

interface TitleProps {
  name: string;
  subtitle: string;
  version?: string;
}

/**
 * Title component - displays main title with stylized text shadows
 * The glow effect is now handled by the CRT shader bloom
 *
 * Layer order (back to front):
 * 1. Deep shadow (darkest) - furthest back
 * 2. Mid shadow - middle
 * 3. Main text (brightest/yellow) - frontmost
 */
export function Title({ name, subtitle, version }: TitleProps) {
  const { width, height } = useCRT();

  const titleY = height / 2 - 300;

  return (
    <Container
      positionType="absolute"
      positionLeft={0}
      positionTop={0}
      width={width}
      height={height}
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start"
    >
      {/* Main title container */}
      <Container
        positionType="absolute"
        positionTop={titleY - 75}
        flexDirection="column"
        alignItems="center"
      >
        {/* Title with shadow layers - all absolute for proper z-stacking */}
        <Container positionType="relative">
          {/* Deep shadow - backmost (rendered first) */}
          <Text
            positionType="absolute"
            positionLeft={6}
            positionTop={6}
            fontSize={150}
            fontWeight="bold"
            color="#aa2010"
            zIndex={0}
          >
            {name}
          </Text>
          {/* Mid shadow - middle layer */}
          <Text
            positionType="absolute"
            positionLeft={3}
            positionTop={3}
            fontSize={150}
            fontWeight="bold"
            color="#ff6030"
            zIndex={1}
          >
            {name}
          </Text>
          {/* Main text - frontmost (bright color triggers shader bloom) */}
          <Text
            positionType="absolute"
            positionLeft={0}
            positionTop={0}
            fontSize={150}
            fontWeight="bold"
            color="#ffe080"
            zIndex={2}
          >
            {name}
          </Text>
          {/* Invisible spacer to maintain layout size */}
          <Text fontSize={150} fontWeight="bold" color="transparent">
            {name}
          </Text>
        </Container>

        {/* Subtitle */}
        <Container positionType="relative" marginTop={4}>
          {/* Shadow - backmost */}
          <Text
            positionType="absolute"
            positionLeft={2}
            positionTop={2}
            fontSize={40}
            color="#1a4080"
            zIndex={0}
          >
            {subtitle}
          </Text>
          {/* Main text - frontmost (bright color triggers shader bloom) */}
          <Text
            positionType="absolute"
            positionLeft={0}
            positionTop={0}
            fontSize={40}
            color="#88ccff"
            zIndex={1}
          >
            {subtitle}
          </Text>
          {/* Invisible spacer to maintain layout size */}
          <Text fontSize={40} color="transparent" zIndex={2}>
            {subtitle}
          </Text>
        </Container>

        {/* Version */}
        {version && (
          <Text marginTop={16} fontSize={20} color="#446688">
            v{version}
          </Text>
        )}
      </Container>
    </Container>
  );
}
